import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { unitsAPI, propertiesAPI, landlordsAPI } from '../lib/api';
import { UNIT_TYPE_LABELS } from '../lib/utils';
import { toast } from 'sonner';
import {
  Plus,
  Home,
  Edit,
  Trash2,
  Loader2,
  Search,
  Building2,
  UserCircle
} from 'lucide-react';

const UNIT_TYPE_ICONS = {
  wohnung: '🏠',
  gewerbe: '🏢',
  stellplatz: '🅿️',
  garage: '🚗',
  lager: '📦'
};

export default function Units() {
  const [units, setUnits] = useState([]);
  const [properties, setProperties] = useState([]);
  const [landlords, setLandlords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPropertyId, setFilterPropertyId] = useState('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingUnit, setEditingUnit] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [formData, setFormData] = useState({
    property_id: '',
    name: '',
    unit_type: 'wohnung',
    area: '',
    floor: '',
    rooms: '',
    landlord_id: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [unitsRes, propsRes, landlordsRes] = await Promise.all([
        unitsAPI.getAll(),
        propertiesAPI.getAll(),
        landlordsAPI.getAll()
      ]);
      setUnits(unitsRes.data);
      setProperties(propsRes.data);
      setLandlords(landlordsRes.data);
    } catch (error) {
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      property_id: properties.length > 0 ? properties[0].id : '',
      name: '',
      unit_type: 'wohnung',
      area: '',
      floor: '',
      rooms: '',
      landlord_id: ''
    });
    setEditingUnit(null);
  };

  const openEditDialog = (unit) => {
    setEditingUnit(unit);
    setFormData({
      property_id: unit.property_id,
      name: unit.name,
      unit_type: unit.unit_type,
      area: unit.area || '',
      floor: unit.floor || '',
      rooms: unit.rooms || '',
      landlord_id: unit.landlord_id || ''
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const data = {
        ...formData,
        area: formData.area ? parseFloat(formData.area) : 0,
        floor: formData.floor || null,
        rooms: formData.rooms ? parseFloat(formData.rooms) : null,
        landlord_id: formData.landlord_id || null
      };

      if (editingUnit) {
        await unitsAPI.update(editingUnit.id, data);
        toast.success('Einheit aktualisiert');
      } else {
        await unitsAPI.create(data);
        toast.success('Einheit erstellt');
      }

      setIsDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    try {
      await unitsAPI.delete(deleteId);
      toast.success('Einheit gelöscht');
      loadData();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    } finally {
      setDeleteId(null);
    }
  };

  const getPropertyName = (propertyId) => {
    const prop = properties.find(p => p.id === propertyId);
    return prop ? prop.name : 'Unbekannt';
  };

  const getLandlordName = (landlordId) => {
    if (!landlordId) return null;
    const landlord = landlords.find(l => l.id === landlordId);
    return landlord ? landlord.name : null;
  };

  const filteredUnits = units.filter(u => {
    const matchesSearch = u.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesProperty = filterPropertyId === 'all' || u.property_id === filterPropertyId;
    return matchesSearch && matchesProperty;
  });

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="units-page" className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Einheiten
            </h1>
            <p className="text-slate-500 mt-1">Verwalten Sie Ihre Mieteinheiten</p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={(open) => {
            setIsDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button 
                data-testid="add-unit-button" 
                className="bg-slate-900 hover:bg-slate-800"
                disabled={properties.length === 0}
              >
                <Plus className="mr-2 h-4 w-4" />
                Einheit hinzufügen
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle>
                  {editingUnit ? 'Einheit bearbeiten' : 'Neue Einheit'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="property_id">
                    Immobilie <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={formData.property_id}
                    onValueChange={(value) => setFormData({ ...formData, property_id: value })}
                    required
                  >
                    <SelectTrigger data-testid="unit-property-select">
                      <SelectValue placeholder="Immobilie auswählen" />
                    </SelectTrigger>
                    <SelectContent>
                      {properties.map((prop) => (
                        <SelectItem key={prop.id} value={prop.id}>
                          {prop.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2 space-y-2">
                    <Label htmlFor="name">
                      Bezeichnung <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="name"
                      data-testid="unit-name-input"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="z.B. Wohnung 1. OG links"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="unit_type">
                      Typ <span className="text-red-500">*</span>
                    </Label>
                    <Select
                      value={formData.unit_type}
                      onValueChange={(value) => setFormData({ ...formData, unit_type: value })}
                    >
                      <SelectTrigger data-testid="unit-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(UNIT_TYPE_LABELS).map(([value, label]) => (
                          <SelectItem key={value} value={value}>
                            {UNIT_TYPE_ICONS[value]} {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="area">
                      Fläche (m²) <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="area"
                      data-testid="unit-area-input"
                      type="number"
                      step="0.01"
                      value={formData.area}
                      onChange={(e) => setFormData({ ...formData, area: e.target.value })}
                      placeholder="75.5"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="floor">Etage</Label>
                    <Input
                      id="floor"
                      data-testid="unit-floor-input"
                      value={formData.floor}
                      onChange={(e) => setFormData({ ...formData, floor: e.target.value })}
                      placeholder="1. OG"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="rooms">Zimmer</Label>
                    <Input
                      id="rooms"
                      data-testid="unit-rooms-input"
                      type="number"
                      step="0.5"
                      value={formData.rooms}
                      onChange={(e) => setFormData({ ...formData, rooms: e.target.value })}
                      placeholder="3"
                    />
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label htmlFor="landlord_id">Vermieter</Label>
                    <Select
                      value={formData.landlord_id}
                      onValueChange={(value) => setFormData({ ...formData, landlord_id: value === 'none' ? '' : value })}
                    >
                      <SelectTrigger data-testid="unit-landlord-select">
                        <SelectValue placeholder="Vermieter auswählen (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Kein Vermieter</SelectItem>
                        {landlords.map((landlord) => (
                          <SelectItem key={landlord.id} value={landlord.id}>
                            {landlord.name} {landlord.company ? `(${landlord.company})` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsDialogOpen(false)}
                  >
                    Abbrechen
                  </Button>
                  <Button type="submit" disabled={saving} data-testid="unit-save-button">
                    {saving ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Speichern...
                      </>
                    ) : (
                      'Speichern'
                    )}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              data-testid="unit-search-input"
              placeholder="Suchen nach Bezeichnung..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={filterPropertyId} onValueChange={setFilterPropertyId}>
            <SelectTrigger className="w-full sm:w-[200px]" data-testid="unit-filter-property">
              <SelectValue placeholder="Alle Immobilien" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Immobilien</SelectItem>
              {properties.map((prop) => (
                <SelectItem key={prop.id} value={prop.id}>
                  {prop.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* No Properties Warning */}
        {properties.length === 0 && (
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="py-4">
              <p className="text-amber-800 text-sm">
                <Building2 className="inline h-4 w-4 mr-2" />
                Erstellen Sie zuerst eine Immobilie, um Einheiten hinzufügen zu können.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Units List */}
        {filteredUnits.length > 0 ? (
          <Card className="border-slate-200">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/50">
                  <TableHead className="font-semibold">Einheit</TableHead>
                  <TableHead className="font-semibold">Immobilie</TableHead>
                  <TableHead className="font-semibold">Vermieter</TableHead>
                  <TableHead className="font-semibold">Typ</TableHead>
                  <TableHead className="font-semibold text-right">Fläche</TableHead>
                  <TableHead className="font-semibold">Etage</TableHead>
                  <TableHead className="w-[100px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUnits.map((unit) => (
                  <TableRow key={unit.id} data-testid={`unit-row-${unit.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center text-lg">
                          {UNIT_TYPE_ICONS[unit.unit_type] || '🏠'}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{unit.name}</p>
                          {unit.rooms && (
                            <p className="text-sm text-slate-500">{unit.rooms} Zimmer</p>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-600">
                      {getPropertyName(unit.property_id)}
                    </TableCell>
                    <TableCell>
                      {getLandlordName(unit.landlord_id) ? (
                        <div className="flex items-center gap-2 text-slate-600">
                          <UserCircle className="h-4 w-4 text-indigo-500" />
                          {getLandlordName(unit.landlord_id)}
                        </div>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="bg-slate-100 text-slate-700">
                        {UNIT_TYPE_LABELS[unit.unit_type] || unit.unit_type}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right tabular-nums font-medium">
                      {unit.area} m²
                    </TableCell>
                    <TableCell className="text-slate-600">
                      {unit.floor || '-'}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          data-testid={`edit-unit-${unit.id}`}
                          onClick={() => openEditDialog(unit)}
                        >
                          <Edit className="h-4 w-4 text-slate-500" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          data-testid={`delete-unit-${unit.id}`}
                          onClick={() => setDeleteId(unit.id)}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        ) : (
          <Card className="border-slate-200">
            <CardContent className="py-12 text-center">
              <Home className="h-12 w-12 mx-auto mb-4 text-slate-300" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">
                {searchTerm || filterPropertyId !== 'all' ? 'Keine Einheiten gefunden' : 'Noch keine Einheiten'}
              </h3>
              <p className="text-slate-500 mb-4">
                {searchTerm || filterPropertyId !== 'all'
                  ? 'Versuchen Sie andere Filtereinstellungen' 
                  : 'Fügen Sie Ihre erste Einheit hinzu'
                }
              </p>
              {!searchTerm && filterPropertyId === 'all' && properties.length > 0 && (
                <Button onClick={() => setIsDialogOpen(true)} data-testid="empty-add-unit-button">
                  <Plus className="mr-2 h-4 w-4" />
                  Erste Einheit hinzufügen
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Delete Confirmation */}
        <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Einheit löschen?</AlertDialogTitle>
              <AlertDialogDescription>
                Diese Aktion kann nicht rückgängig gemacht werden. Alle zugehörigen 
                Mieter werden ebenfalls gelöscht.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Abbrechen</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700"
                data-testid="confirm-delete-unit"
              >
                Löschen
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
